import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { useLists } from '../contexts/ListsContext';
import api from '../services/api';
import { trackListFollow, trackListUnfollow, trackPlaceClick, trackListLinkClick } from '../services/analytics';

// Extracted components
import {
  PlaceCard,
  ListDetailHeader,
  ListDetailTabs,
  SearchFilterBar,
  ShareModal,
} from '../components/ListDetail';

import { PlaceListSkeleton, PlaceCardSkeleton, Skeleton } from '../components/Skeleton';
import ImportPlacesModal from '../components/ImportPlacesModal';

// Custom hooks
import {
  useListDetail,
  useUserLocation,
  usePlacesWithDistances,
  useFilteredPlaces,
} from '../hooks/useListDetail';

// Utils
import { splitPlaceAddress } from '../utils/locationUtils';
import { formatPriceLevel, getSubcategoryFromType } from '../services/googleMaps';

import '../styles/ListDetail.css';

function ListDetail() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { canAccess } = useSubscription();
  const { showAlert, showToast } = useNotificationModal();
  const { getDesignation, ensureDesignationsLoaded } = useUserDesignations();
  const { leaveCollaboration } = useLists();

  // Premium feature flags
  const canShowPhotos = canAccess('photos');
  const canShowOpeningHours = canAccess('openingHours');
  const canSortByOpen = canAccess('sortByOpen');
  const canFilterByOpenAt = canAccess('filterByOpenAt');
  const canPublicShare = canAccess('publicSharing');
  const canViewMap = canAccess('mapView');

  // Custom hooks for data fetching
  const {
    list,
    places,
    isOwnList,
    loadingList,
    listError,
    loadingMoreItems,
    totalPlaceCount,
    isFollowing,
    setIsFollowing,
    followLoading,
    setFollowLoading,
    notificationsMuted,
    setNotificationsMuted,
    notificationToggleLoading,
    setNotificationToggleLoading,
    localFollowersCount,
    setLocalFollowersCount,
    userPlacesMap,
    friendsPlacesMap,
    placeStatuses,
    isPlaceOpen,
    getOpenStatusIndicator,
    fetchLists,
    refetchList,
    user,
  } = useListDetail(listId);

  const userLocation = useUserLocation();
  const placesWithDistances = usePlacesWithDistances(places, userLocation);

  // UI state
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('default');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [overflowingElements, setOverflowingElements] = useState(new Set());
  const [openAtFilter, setOpenAtFilter] = useState({ days: [], slots: [] });

  // Share link state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Import places state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Leave collaboration state
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Refs
  const elementRefs = useRef({});
  const sortDropdownRef = useRef(null);

  // Initialize share token from list
  useEffect(() => {
    if (list && list.shareToken && list.isPubliclyShared) {
      setShareToken(list.shareToken);
    }
  }, [list]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);

  // Ensure user designations are loaded (for displaying badges)
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  // Check overflow after render and on window resize
  const checkOverflow = useCallback(() => {
    const overflowing = new Set();
    Object.entries(elementRefs.current).forEach(([key, element]) => {
      if (element && element.scrollWidth > element.clientWidth) {
        overflowing.add(key);
      }
    });
    setOverflowingElements(overflowing);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow, places]);

  // Determine if user is a collaborator on this list
  // Note: list.collaborators is an array of user ID strings, not objects
  const isUserCollaborator = list?.collaborators?.includes(user?.userId) || list?.collaborators?.includes(user?.id) || false;

  // Check if Open At filter is active
  const isOpenAtActive = openAtFilter.days.length > 0 && openAtFilter.slots.length > 0;

  // Filter places
  const filteredPlaces = useFilteredPlaces({
    placesWithDistances,
    activeTab,
    searchQuery,
    sortOption,
    userLocation,
    userPlacesMap,
    isPlaceOpen,
    getDesignation,
    isOwnList,
    isUserCollaborator,
    openAtFilter: isOpenAtActive ? openAtFilter : null,
    placeStatuses,
  });

  // Calculate match count for Open At filter (excluding places without hours)
  const openAtMatchCount = isOpenAtActive
    ? filteredPlaces.filter(p => !p._hoursUnknown).length
    : null;

  // Handler for Open At filter changes
  const handleOpenAtFilterChange = (filter) => {
    setOpenAtFilter(filter);
  };

  // Handler for clearing Open At filter
  const handleClearOpenAtFilter = () => {
    setOpenAtFilter({ days: [], slots: [] });
  };

  // Handlers
  const handlePlaceClick = (place) => {
    // Track place click analytics (only for external lists)
    if (!isOwnList && list?.userId) {
      trackPlaceClick(listId, list.userId, place.placeId);
    }

    navigate(`/place/${place.placeId}`, {
      state: {
        place,
        places: filteredPlaces,
        listId,
        listOwnerId: list?.userId,
        listOwnerUsername: list?.username,
        listOwnerDisplayName: list?.creatorName,
        listOwnerProfilePhoto: list?.creatorPhotoURL,
        returnPath: `/list/${listId}`,
        isOwnList,
        isCollaborationList: list?.collaborators?.length > 0,
        isUserCollaborator,
        collaboratorIds: list?.collaborators || [],
      }
    });
  };

  const handleBackClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    navigate(-1);
  };

  const handleFollowToggle = async () => {
    if (followLoading) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await api.unfollowList(listId);
        setIsFollowing(false);
        setLocalFollowersCount(prev => Math.max(0, (prev || 0) - 1));
        // Track unfollow analytics
        if (list?.userId) {
          trackListUnfollow(listId, list.userId);
        }
      } else {
        await api.followList(listId);
        setIsFollowing(true);
        setLocalFollowersCount(prev => (prev || 0) + 1);
        // Track follow analytics
        if (list?.userId) {
          trackListFollow(listId, list.userId);
        }
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      showAlert(error.message || 'Failed to update follow status', { variant: 'error' });
    } finally {
      setFollowLoading(false);
    }
  };

  const handleNotificationToggle = async () => {
    // Allow toggle for both followers and collaborators
    if (notificationToggleLoading || (!isFollowing && !isUserCollaborator)) return;
    setNotificationToggleLoading(true);
    try {
      const response = await api.toggleListNotifications(listId, !notificationsMuted);
      setNotificationsMuted(response.data?.notificationsMuted || false);
    } catch (error) {
      console.error('Failed to toggle notifications:', error);
      showAlert(error.message || 'Failed to update notification settings', { variant: 'error' });
    } finally {
      setNotificationToggleLoading(false);
    }
  };

  const handleLeaveCollaboration = async () => {
    if (leaveLoading) return;
    setLeaveLoading(true);
    try {
      await leaveCollaboration(listId);
      showToast('You have left this collaboration');
      navigate('/my-lists');
    } catch (error) {
      console.error('Failed to leave collaboration:', error);
      showAlert(error.message || 'Failed to leave collaboration', { variant: 'error' });
    } finally {
      setLeaveLoading(false);
      setShowLeaveConfirm(false);
    }
  };

  const handleGenerateShareLink = async () => {
    if (!canPublicShare) {
      navigate('/settings/premium');
      return;
    }
    setShareLoading(true);
    try {
      const response = await api.generateShareLink(listId);
      setShareToken(response.data.shareToken);
    } catch (error) {
      console.error('Failed to generate share link:', error);
      showAlert(error.message || 'Failed to generate share link', { variant: 'error' });
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShareLink = async () => {
    setShareLoading(true);
    try {
      await api.revokeShareLink(listId);
      setShareToken(null);
    } catch (error) {
      console.error('Failed to revoke share link:', error);
      showAlert(error.message || 'Failed to revoke share link', { variant: 'error' });
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleManagePlace = (place) => {
    // Determine source list info for analytics tracking (when saving from someone else's list)
    const sourceListInfo = (!isOwnList && !isUserCollaborator && listId && list?.userId) ? {
      sourceListId: listId,
      sourceListOwnerId: list.userId,
    } : null;

    navigate('/place/save', {
      replace: true,
      state: {
        place,
        existingListInfo: place?.placeId ? userPlacesMap[place.placeId] : null,
        returnPath: `/list/${listId}`,
        sourceListInfo,
      }
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    setShowSortDropdown(false);
  };

  // Loading state
  if (loadingList) {
    return (
      <div className="list-detail-container">
        <div className="detail-header">
          <div className="header-back-row">
            <button className="back-btn" onMouseDown={handleBackClick} aria-label="Go back">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
          <Skeleton height="28px" width="60%" borderRadius="4px" className="skeleton-header-title" />
          <Skeleton height="14px" width="40%" borderRadius="4px" className="skeleton-header-subtitle" />
        </div>
        <div className="content-wrapper">
          <div className="list-stats-skeleton">
            <Skeleton height="16px" width="120px" borderRadius="4px" />
          </div>
          <PlaceListSkeleton count={4} />
        </div>
      </div>
    );
  }

  // Error state
  if (listError) {
    return (
      <div className="list-detail-container">
        <div className="detail-header">
          <div className="header-back-row">
            <button className="back-btn" onMouseDown={handleBackClick} aria-label="Go back">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </div>
        <div className="content-wrapper">
          <p style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>Error: {listError}</p>
        </div>
      </div>
    );
  }

  // No list found state
  if (!list) {
    return (
      <div className="list-detail-container">
        <div className="detail-header">
          <div className="header-back-row">
            <button className="back-btn" onMouseDown={handleBackClick} aria-label="Go back">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
          <Skeleton height="28px" width="60%" borderRadius="4px" className="skeleton-header-title" />
          <Skeleton height="14px" width="40%" borderRadius="4px" className="skeleton-header-subtitle" />
        </div>
        <div className="content-wrapper">
          <div className="list-stats-skeleton">
            <Skeleton height="16px" width="120px" borderRadius="4px" />
          </div>
          <PlaceListSkeleton count={4} />
        </div>
      </div>
    );
  }

  return (
    <div className="list-detail-container">
      <ListDetailHeader
        list={list}
        isOwnList={isOwnList}
        isCollaborationList={list?.collaborators?.length > 0}
        isUserCollaborator={isUserCollaborator}
        isFollowing={isFollowing}
        followLoading={followLoading}
        notificationsMuted={notificationsMuted}
        notificationToggleLoading={notificationToggleLoading}
        shareToken={shareToken}
        canPublicShare={canPublicShare}
        canViewMap={canViewMap}
        canTripPlanner={canAccess('tripPlanner')}
        canOfflineDownload={canAccess('offlineDownload')}
        localFollowersCount={localFollowersCount}
        listId={listId}
        leaveLoading={leaveLoading}
        showLeaveConfirm={showLeaveConfirm}
        onEditList={() => navigate(`/list/${listId}/edit`, { replace: true })}
        onAddPlace={() => navigate(`/list/${listId}/add-place`, { replace: true })}
        onDuplicate={() => navigate(`/list/${listId}/duplicate`, { replace: true, state: { list } })}
        onImportPlaces={() => setIsImportModalOpen(true)}
        onShare={() => setIsShareModalOpen(true)}
        onTripPlan={() => navigate(`/list/${listId}/trip`)}
        onFollowToggle={handleFollowToggle}
        onNotificationToggle={handleNotificationToggle}
        onLeaveClick={() => setShowLeaveConfirm(true)}
        onLeaveConfirm={handleLeaveCollaboration}
        onLeaveCancel={() => setShowLeaveConfirm(false)}
        onBack={handleBackClick}
        onExternalLinkClick={() => {
          // Track external link click (only for other users' lists)
          if (!isOwnList && list?.userId) {
            trackListLinkClick(listId, list.userId);
          }
        }}
      />

      <div className="content-wrapper">
        <ListDetailTabs
          activeTab={activeTab}
          isOwnList={isOwnList}
          isUserCollaborator={isUserCollaborator}
          onTabChange={handleTabChange}
          places={places}
          totalPlaceCount={totalPlaceCount}
          userPlacesMap={userPlacesMap}
          getDesignation={getDesignation}
        />

        {places.length > 0 && (
          <SearchFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortOption={sortOption}
            onSortChange={handleSortChange}
            showSortDropdown={showSortDropdown}
            onToggleSortDropdown={() => setShowSortDropdown(!showSortDropdown)}
            sortDropdownRef={sortDropdownRef}
            userLocation={userLocation}
            canSortByOpen={canSortByOpen}
            canFilterByOpenAt={canFilterByOpenAt}
            openAtFilter={openAtFilter}
            onOpenAtFilterChange={handleOpenAtFilterChange}
            onClearOpenAtFilter={handleClearOpenAtFilter}
            openAtMatchCount={openAtMatchCount}
          />
        )}

        <div className="places-list">
          {places.length === 0 ? (
            // Empty state when list has no places
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <p className="empty-state-title">
                {isOwnList || isUserCollaborator
                  ? "Nothing to see here..."
                  : "This list is empty"}
              </p>
              {(isOwnList || isUserCollaborator) && (
                <p className="empty-state-subtitle">
                  ...let's change that by tapping{' '}
                  <span className="empty-state-button-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </span>
                  {' '} above and adding your first place!
                </p>
              )}
            </div>
          ) : filteredPlaces.length === 0 && isOpenAtActive ? (
            // Empty state when Open At filter returns no results
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <circle cx="12" cy="16" r="2"/>
                </svg>
              </div>
              <p className="empty-state-title">No places open at selected times</p>
              <p className="empty-state-subtitle">
                Try different days or time slots, or{' '}
                <button
                  className="empty-state-link"
                  onClick={handleClearOpenAtFilter}
                >
                  clear the filter
                </button>
              </p>
            </div>
          ) : (
            filteredPlaces.map((place) => {
              // Items still awaiting enriched place data (streamed in pages)
              // carry only placeId/badge - render a skeleton that upgrades in
              // place once its page arrives
              if (!place.name) {
                return <PlaceCardSkeleton key={place.id || place.placeId} />;
              }

              const openStatus = getOpenStatusIndicator(place);
              const priceLevel = formatPriceLevel(place.priceLevel);
              const subcategory = getSubcategoryFromType(place.placeType);
              const { street, cityState } = splitPlaceAddress(place.address, place.addressComponents);

              return (
                <PlaceCard
                  key={place.id}
                  place={place}
                  isOwnList={isOwnList}
                  isCollaborationList={list?.collaborators?.length > 0}
                  isUserCollaborator={isUserCollaborator}
                  canShowPhotos={canShowPhotos}
                  canShowOpeningHours={canShowOpeningHours}
                  openStatus={openStatus}
                  priceLevel={priceLevel}
                  subcategory={subcategory}
                  street={street}
                  cityState={cityState}
                  userDesignation={getDesignation(place.placeId)}
                  userPlacesMap={userPlacesMap}
                  friendsPlacesMap={friendsPlacesMap}
                  overflowingElements={overflowingElements}
                  elementRefs={elementRefs}
                  onPlaceClick={handlePlaceClick}
                  onManagePlace={handleManagePlace}
                  hoursUnknown={place._hoursUnknown}
                />
              );
            })
          )}
          {/* Placeholder rows for items whose pages are still streaming in
              (external lists render only the rows received so far) */}
          {loadingMoreItems && totalPlaceCount > places.length && (
            Array.from({ length: Math.min(totalPlaceCount - places.length, 6) }).map((_, i) => (
              <PlaceCardSkeleton key={`pending-${i}`} />
            ))
          )}
        </div>
      </div>

      {/* Modals */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        canPublicShare={canPublicShare}
        shareToken={shareToken}
        shareLoading={shareLoading}
        shareCopied={shareCopied}
        onGenerateShareLink={handleGenerateShareLink}
        onRevokeShareLink={handleRevokeShareLink}
        onCopyShareLink={handleCopyShareLink}
      />

      <ImportPlacesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        destinationListId={listId}
        destinationListTitle={list?.title || list?.name}
        onImportComplete={(result) => {
          // Refresh the list to show imported places with full details
          refetchList();
          showToast(`Imported ${result.imported} place${result.imported !== 1 ? 's' : ''}`);
        }}
      />
    </div>
  );
}

export default ListDetail;
